import {
	AfterViewChecked,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	ViewChild,
} from '@angular/core';
import { BreakPointService, ButtonComponent, CollapsibleNavComponent, Device } from '@woolworthsnz/styleguide';
import { BoostOfferProductTag, FilterCriteria } from '@woolworthsnz/trader-api';
import { ActivatedRoute, RouterLink, UrlSegment } from '@angular/router';
import { SearchFilterKeys } from '../enums';
import { SearchQueryParams } from '../models/search-query-params.model';
import { FilterDisclaimerComponent } from '../filter-disclaimer/filter-disclaimer.component';
import { FilterTagsComponent } from '../filter-tags/filter-tags.component';
import { AsyncPipe, JsonPipe, NgFor, NgIf, TitleCasePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface FilterChangeArgs {
	filter: FilterCriteria;
	isSelected: boolean;
	url: UrlSegment[];
}

@Component({
	// tslint:disable-next-line:component-selector
	selector: 'search-das-filter',
	templateUrl: './search-das-filter.component.html',
	styleUrls: ['./search-das-filter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		CollapsibleNavComponent,
		NgFor,
		RouterLink,
		FilterTagsComponent,
		FilterDisclaimerComponent,
		ButtonComponent,
		AsyncPipe,
		JsonPipe,
		TitleCasePipe,
	],
})
export class SearchDasFilterComponent implements AfterViewChecked {
	@ViewChild('nav') navRef: ElementRef;

	@Input() boostOfferTag?: BoostOfferProductTag;
	@Input() dasFacets: FilterCriteria[] = [];
	@Input() dasFacetsRoute: string[] = [];
	@Input() dasFacetsQueryParams: SearchQueryParams[] = [];
	@Input() filterGroups: Array<FilterCriteria[]> = new Array<FilterCriteria[]>();
	@Input() selectedFilters?: FilterCriteria[];
	@Input() selectedDasFacets?: FilterCriteria[];
	@Input() marginTop = 0;

	@Output() filterChange = new EventEmitter<FilterChangeArgs>();
	@Output() facetChange = new EventEmitter<FilterCriteria>();
	@Output() clearFilters = new EventEmitter<void>();

	device = Device;
	currentDevice: Device;
	device$ = this.breakPointService.device$.pipe(takeUntilDestroyed());

	get url(): UrlSegment[] {
		return this.route.snapshot && this.route.snapshot.url;
	}

	constructor(public breakPointService: BreakPointService, private route: ActivatedRoute) {
		this.device$.subscribe((device) => (this.currentDevice = device));
	}

	ngAfterViewChecked(): void {
		this.setNavMargin();
	}

	facetsTrackFn = (_: any, facet: { name: any }): any => facet.name;

	onFilterChanged = (event: Event): void => {
		const isSelected = (<HTMLInputElement>event.target).checked;
		const filterData = (<HTMLInputElement>event.target).dataset.filterData;

		this.filterChange.emit({
			filter: JSON.parse(filterData || ''),
			isSelected,
			url: this.url,
		});
	};

	handleClickDasFacet = (event: Event): void => {
		event.preventDefault();
	};

	onFacetChanged = (event: Event): void => {
		const dasFilter = this.getFacetDataFromElement(<HTMLInputElement>event.target);
		this.facetChange.emit(dasFilter);
	};

	private getFacetDataFromElement(el: HTMLInputElement): FilterCriteria {
		return JSON.parse(el.dataset.facetData || '');
	}

	// TODO Needs to create a service to share business logic(pure functions) between the components
	// placeholder ticket https://woolworthslimited.atlassian.net/browse/POD-5629
	canShowDisclaimer = (filter: FilterCriteria): boolean => filter?.key === SearchFilterKeys.PiesAttributes;

	onClearFilters(): void {
		this.clearFilters.emit();
	}

	isFilterSelected(filter: FilterCriteria): boolean {
		if (!this.selectedFilters) {
			return false;
		}

		return (
			this.selectedFilters.findIndex(
				({ key, value }: { [key: string]: any }) => key === filter.key && value === filter.value
			) !== -1
		);
	}

	onRemoveFilter(filterCriteria: FilterCriteria): void {
		this.facetChange.emit(filterCriteria);
	}

	onRemoveAllFilters(): void {
		this.clearFilters.emit();
	}

	private setNavMargin(): void {
		const currentNavMargin = this.navRef.nativeElement.style.marginTop;
		const isMarginEmpty = currentNavMargin === '' || currentNavMargin === '0px';
		const isMarginEqualToHeaderHeight = currentNavMargin === this.marginTop;
		if (this.currentDevice !== Device.MOBILE && !isMarginEqualToHeaderHeight) {
			this.navRef.nativeElement.style.marginTop = this.marginTop + 'px';
		}

		if (this.currentDevice === Device.MOBILE && !isMarginEmpty) {
			this.navRef.nativeElement.style.marginTop = '0px';
		}
	}
}
